/**
 * @fileoverview Atom: the only clickable action primitive of the app.
 * @module components/atoms/Button
 */

import type { ButtonHTMLAttributes, ReactNode } from "react"

import "./Button.css"

/** Visual weight of the action. */
export type ButtonVariant = "primary" | "secondary" | "ghost"

/** Physical size of the control. */
export type ButtonSize = "sm" | "md" | "lg"

/** Props accepted by {@link Button}. */
export interface ButtonProps extends Omit<
	ButtonHTMLAttributes<HTMLButtonElement>,
	"className"
> {
	/** Label or icon rendered inside the control. */
	readonly children: ReactNode
	/** Visual weight. Defaults to `primary`. */
	readonly variant?: ButtonVariant
	/** Physical size. Defaults to `md`. */
	readonly size?: ButtonSize
	/** Accessible label, required when the content is not descriptive text. */
	readonly ariaLabel?: string
	/** True for a square control holding only an {@link Icon}. */
	readonly iconOnly?: boolean
	/** True for a destructive action (delete, discard). */
	readonly danger?: boolean
}

/**
 * Action button.
 *
 * @param props - Variant, size, accessible label and native button props.
 * @returns The rendered control.
 *
 * @example
 * <Button variant="secondary" size="lg" onClick={retry}>Réessayer</Button>
 */
export function Button({
	children,
	variant = "primary",
	size = "md",
	ariaLabel,
	iconOnly = false,
	danger = false,
	type = "button",
	...rest
}: ButtonProps) {
	const classNames = [
		"button",
		`button--${variant}`,
		`button--${size}`,
		iconOnly ? "button--icon" : null,
		danger ? "button--danger" : null,
	]
		.filter((value): value is string => value !== null)
		.join(" ")

	return (
		<button
			type={type}
			className={classNames}
			{...(ariaLabel === undefined ? {} : { "aria-label": ariaLabel })}
			{...rest}
		>
			{children}
		</button>
	)
}
